import React, { useEffect } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import * as Sentry from '@sentry/react';
import { isLocalApp } from '../../utils/helpers';
import { isProd } from '../../utils/helpers/environment';

export const ErrorFallback: React.FC<FallbackProps> = ({ error }) => {
  const { t } = useTranslation('common');

  useEffect(() => {
    if (!isLocalApp) {
      Sentry.captureMessage('Error Boundary', {
        level: 'info',
        extra: {
          message: error?.message || 'No error message',
        },
      });
    }
  }, []);

  const tryAgain = () => {
    window.location.reload();
  };

  const returnToHome = () => {
    window.location.href = '/';
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        bgcolor: 'var(--color-element)',
        color: 'var(--color-type-primary)',
        flexDirection: 'column',
        p: 3,
        textAlign: 'center',
      }}
    >
      <Box mb={3}>
        <ErrorOutlineIcon sx={{ fontSize: 64, color: '#f87171' }} />
      </Box>
      <Typography variant="h5" fontWeight={700} gutterBottom color="var(--color-type-primary)">
        {t('errors.somethingWentWrong')}
      </Typography>
      {!isProd && error?.message && (
        <Box
          component="pre"
          sx={{
            color: '#ef4444',
            background: '#fef2f2',
            p: 2,
            borderRadius: 2,
            my: 2,
            fontSize: 14,
            overflowX: 'auto',
            textAlign: 'left',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            maxHeight: 200,
          }}
        >
          {error.message}
        </Box>
      )}
      <Stack direction="row" spacing={2} justifyContent="center" mt={3}>
        <button className="button button--secondary" style={{ minWidth: 120 }} onClick={tryAgain}>
          {t('buttons.tryAgain')}
        </button>
        <button className="button" style={{ minWidth: 120 }} onClick={returnToHome}>
          {t('buttons.return')}
        </button>
      </Stack>
    </Box>
  );
};
